import type { User } from '@auth/domain/User';
import { useAuthState } from '@auth/hooks';
import { useAvatarColor } from '@core/hooks/useAvatarColor';
import { useUserPreferences } from '@core/hooks/useUserPreferences';
import { getInitials } from '@core/utils/getInitials';
import { Badge, BadgeSize, BadgeVariant, Gamerpic } from '@soteria/design-system-ve';
import { useCallback } from 'react';

import {
  accountAvatar,
  accountAvatarImage,
  accountAvatarPlaceholder,
  accountEmail,
  accountHeader,
  accountName,
  accountRoleBadge,
  accountRolesContainer,
} from './AccountTab.css';
import { ColorPicker } from './ColorPicker';
import { settingsOptionContainer, settingsOptionLabel } from './SettingsDialog.css';

interface AccountTabProps {
  className?: string;
}

interface RolesBadgesProps {
  roles: string[];
  isSystemAdmin: boolean;
}

/**
 * Renders role badges for the user
 */
function RolesBadges({ roles, isSystemAdmin }: RolesBadgesProps) {
  const hasRoles = roles.length > 0 || isSystemAdmin;

  return (
    <div aria-labelledby="account-roles-label" className={accountRolesContainer} role="list">
      {isSystemAdmin && (
        <span role="listitem">
          <Badge className={accountRoleBadge} size={BadgeSize.Small} text="System Admin" variant={BadgeVariant.Approved} />
        </span>
      )}
      {roles.map(role => (
        <span key={role} role="listitem">
          <Badge className={accountRoleBadge} size={BadgeSize.Small} text={role} variant={BadgeVariant.Default} />
        </span>
      ))}
      {!hasRoles && <span role="listitem">No roles assigned</span>}
    </div>
  );
}

interface AccountDetailsProps {
  user: User;
  avatarBackgroundColor: string;
  isSystemAdmin: boolean;
  palette: readonly string[];
  selectedColorIndex: number | null;
  onColorSelect: (index: number | null) => void;
}

/**
 * Renders the account details content
 */
function AccountDetails({ user, avatarBackgroundColor, isSystemAdmin, palette, selectedColorIndex, onColorSelect }: AccountDetailsProps) {
  const userName = user.name || 'Unknown User';
  const userRoles = user.roles || [];

  return (
    <>
      {/* Profile Header with Avatar */}
      <div className={accountHeader}>
        <Gamerpic
          className={accountAvatar}
          gamerpicImageComponent={
            user.picture ? (
              <img alt={userName} className={accountAvatarImage} src={user.picture} />
            ) : (
              <div aria-hidden="true" className={accountAvatarPlaceholder} style={{ backgroundColor: avatarBackgroundColor }}>
                {getInitials(userName)}
              </div>
            )
          }
          imageAlt={`${userName} profile`}
        />
        <div>
          <div className={accountName}>{userName}</div>
          {user.email && <div className={accountEmail}>{user.email}</div>}
        </div>
      </div>

      {/* Name */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>Name</div>
        <div className={settingsOptionLabel}>{userName}</div>
      </div>

      {/* Email */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>Email</div>
        <div className={settingsOptionLabel}>{user.email || 'Not provided'}</div>
      </div>

      {/* User ID */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel}>User ID</div>
        <div className={settingsOptionLabel}>{user.id}</div>
      </div>

      {/* Roles */}
      <div className={settingsOptionContainer}>
        <div className={settingsOptionLabel} id="account-roles-label">
          Roles
        </div>
        <RolesBadges isSystemAdmin={isSystemAdmin} roles={userRoles} />
      </div>

      {/* Avatar Color Picker */}
      <ColorPicker palette={palette} selectedColorIndex={selectedColorIndex} onColorSelect={onColorSelect} />
    </>
  );
}

/**
 * AccountTab component displays user account information in the Settings dialog.
 * Shows: name, email, profile picture, and roles.
 */
export function AccountTab({ className }: AccountTabProps) {
  const { user, isUserSystemAdmin } = useAuthState();
  const { avatarColorIndex, setAvatarColorIndex } = useUserPreferences();
  const { avatarBackgroundColor, palette } = useAvatarColor(user?.name, user?.email);

  const handleColorSelect = useCallback(
    (index: number | null) => {
      setAvatarColorIndex(index);
    },
    [setAvatarColorIndex]
  );

  if (!user) {
    return (
      <div className={className}>
        <div className={settingsOptionContainer}>
          <div className={settingsOptionLabel}>Not signed in</div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <AccountDetails
        avatarBackgroundColor={avatarBackgroundColor}
        isSystemAdmin={isUserSystemAdmin}
        palette={palette}
        selectedColorIndex={avatarColorIndex}
        user={user}
        onColorSelect={handleColorSelect}
      />
    </div>
  );
}
